import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Typography, Space, Card, Tag, Tooltip } from "antd";
import {
  ReloadOutlined,
  StepForwardOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  TableOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Mafs, Coordinates, Polygon, Line, Text, Theme } from "mafs";

const { Title, Paragraph, Text: AntText } = Typography;

/**
 * TransformationGrid Flagship Component
 *
 * Fixes vs the pasted version:
 * - ✅ Adds DEFAULT export (prevents "type is invalid ... got undefined" when loader expects default)
 * - ✅ Pause now actually stops the interval (no runaway playback)
 * - ✅ Normalises op.type and op.axis (handles enum strings like Y_EQUALS_X, Y=-X, spaces/dashes)
 * - ✅ Implements REFLECTION for X, Y, Y_EQUALS_X, Y_EQUALS_NEG_X with reflectionOffset (k)
 * - ✅ Makes currentOp null when complete (UI shows "Complete" instead of repeating last op)
 */
export const TransformationGrid = ({ config, content }) => {
  const cfg = config || {};
  const explanation =
      content?.explanation ||
      "Follow the transformations to see how the shape moves on the grid.";

  // --- Logic Helpers ---
  const toPointList = (shape) => {
    if (!Array.isArray(shape) || shape.length < 3)
      return [
        [-2, -1],
        [2, -1],
        [0, 2],
      ];
    return shape.map((p) => [Number(p.x), Number(p.y)]);
  };

  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

  const normType = (t) => {
    const v = (t ?? "TRANSLATION")
        .toString()
        .trim()
        .toUpperCase()
        .replace(/[-\s]+/g, "_");
    if (v === "ENLARGEMENT" || v === "SCALE" || v === "SCALING") return "DILATION";
    return v;
  };

  const normAxis = (a) => {
    if (a == null) return "X";
    const raw = a.toString().trim().toUpperCase().replace(/\s+/g, "");
    if (raw === "Y=X" || raw === "YEQUALSX" || raw === "Y=X+0") return "Y_EQUALS_X";
    if (raw === "Y=-X" || raw === "YEQUALS-X" || raw === "Y=-X+0") return "Y_EQUALS_NEG_X";
    return raw.replace(/-/g, "_");
  };

  const safeNum = (v, fallback = 0) =>
      Number.isFinite(Number(v)) ? Number(v) : fallback;

  // --- State ---
  const basePts = useMemo(() => toPointList(cfg.originalShape), [cfg.originalShape]);

  const ops = useMemo(() => {
    const o = cfg.operations || (cfg.operation ? [cfg.operation] : null);
    return Array.isArray(o) && o.length
        ? o
        : [{ type: "TRANSLATION", vector: [2, 1] }];
  }, [cfg.operations, cfg.operation]);

  // appliedSteps = how many operations have been committed to the "before" shape
  const [appliedSteps, setAppliedSteps] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const playRef = useRef(null);

  const stopPlayback = () => {
    if (playRef.current) clearInterval(playRef.current);
    playRef.current = null;
    setIsPlaying(false);
  };

  useEffect(() => {
    return () => stopPlayback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Transformation Logic ---
  const reflectAcrossLine = (x, y, a, b, c) => {
    // Reflect point across ax + by + c = 0
    const denom = a * a + b * b;
    if (denom === 0) return [x, y];
    const d = (a * x + b * y + c) / denom;
    return [x - 2 * a * d, y - 2 * b * d];
  };

  const applyOp = (pts, op) => {
    if (!op) return pts;

    const type = normType(op.type);
    const vec = Array.isArray(op.vector) ? op.vector : [0, 0];
    const dx = safeNum(vec[0], 0);
    const dy = safeNum(vec[1], 0);

    const center = Array.isArray(op.center) ? op.center : [0, 0];
    const cx = safeNum(center[0], 0);
    const cy = safeNum(center[1], 0);

    const angle = safeNum(op.angle, 0);
    const scale = safeNum(op.scale, 1);

    const k = safeNum(op.reflectionOffset, 0);
    const axis = normAxis(op.axis);

    switch (type) {
      case "TRANSLATION":
        return pts.map(([x, y]) => [x + dx, y + dy]);

      case "ROTATION": {
        const rad = (angle * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        return pts.map(([x, y]) => {
          const ox = x - cx;
          const oy = y - cy;
          return [cx + ox * cos - oy * sin, cy + ox * sin + oy * cos];
        });
      }

      case "DILATION":
        return pts.map(([x, y]) => [cx + (x - cx) * scale, cy + (y - cy) * scale]);

      case "REFLECTION":
        return pts.map(([x, y]) => {
          // Java meaning:
          // X => reflect across y = k
          // Y => reflect across x = k
          // Y_EQUALS_X => reflect across y = x + k
          // Y_EQUALS_NEG_X => reflect across y = -x + k
          if (axis === "X") return [x, 2 * k - y];
          if (axis === "Y") return [2 * k - x, y];
          if (axis === "Y_EQUALS_X") {
            // y - x - k = 0  => a=-1, b=1, c=-k
            return reflectAcrossLine(x, y, -1, 1, -k);
          }
          if (axis === "Y_EQUALS_NEG_X") {
            // y + x - k = 0 => a=1, b=1, c=-k
            return reflectAcrossLine(x, y, 1, 1, -k);
          }
          return [x, y];
        });

      default:
        return pts;
    }
  };

  const getPointsAtStep = (stepCount) => {
    let currentPts = basePts;
    const n = clamp(stepCount, 0, ops.length);
    for (let i = 0; i < n; i++) currentPts = applyOp(currentPts, ops[i]);
    return currentPts;
  };

  const beforePts = useMemo(() => getPointsAtStep(appliedSteps), [basePts, ops, appliedSteps]);

  const afterPts = useMemo(() => {
    // Show the *next* op preview (mapping arrows) unless we're complete
    if (appliedSteps >= ops.length) return beforePts;
    return applyOp(beforePts, ops[appliedSteps]);
  }, [beforePts, ops, appliedSteps]);

  const currentOp = appliedSteps >= ops.length ? null : ops[appliedSteps];

  // --- Actions ---
  const reset = () => {
    stopPlayback();
    setAppliedSteps(0);
  };

  const onPlay = () => {
    // Prevent multiple intervals
    stopPlayback();

    // If complete, restart
    if (appliedSteps >= ops.length) setAppliedSteps(0);

    setIsPlaying(true);

    playRef.current = setInterval(() => {
      setAppliedSteps((prev) => {
        // When we are previewing the last step (prev === ops.length - 1), next tick completes (ops.length)
        if (prev >= ops.length - 1) {
          // Stop after completing
          if (playRef.current) clearInterval(playRef.current);
          playRef.current = null;
          setIsPlaying(false);
          return ops.length;
        }
        return prev + 1;
      });
    }, 1000);
  };

  // Dynamic ViewBox to keep all shapes visible
  const viewBox = useMemo(() => {
    const all = [...beforePts, ...afterPts];
    const xs = all.map((p) => p[0]);
    const ys = all.map((p) => p[1]);
    const minX = Math.min(...xs, -5) - 2;
    const maxX = Math.max(...xs, 5) + 2;
    const minY = Math.min(...ys, -5) - 2;
    const maxY = Math.max(...ys, 5) + 2;
    return { x: [minX, maxX], y: [minY, maxY] };
  }, [beforePts, afterPts]);

  const stepLabel = appliedSteps >= ops.length ? ops.length : appliedSteps + 1;

  return (
      <div className="ch-tx-widget animate-slide-up">
        <Space orientation="vertical" style={{ width: "100%" }} size="middle">
          {/* Header Branding */}
          <Card size="small" className="ch-tx-header">
            <div className="flexSpaceBetween flexAlignCentre">
              <Space>
                <Title level={4} style={{ margin: 0 }}>
                  Transformation Grid
                </Title>
                <Tag color="blue">
                  Step {stepLabel} of {ops.length}
                </Tag>
                {appliedSteps >= ops.length && <Tag color="success">Complete</Tag>}
              </Space>
              <Button icon={<ReloadOutlined />} size="small" onClick={reset}>
                Reset
              </Button>
            </div>
          </Card>

          <Paragraph type="secondary" italic style={{ marginBottom: 0 }}>
            {explanation}
          </Paragraph>

          {/* Rule Display */}
          <div className="ch-tx-rule">
          <span className="ch-tx-rule-label">
            {appliedSteps >= ops.length ? "Complete" : `Step ${stepLabel}:`}
          </span>
            <AntText strong>{currentOp?.type || "—"}</AntText>
            <Tooltip title="This visualization shows the mapping of each vertex from the pre-image (grey) to the image (blue).">
              <InfoCircleOutlined
                  style={{
                    color: "var(--on-surface-mid-colour)",
                    cursor: "help",
                    marginLeft: 8,
                  }}
              />
            </Tooltip>
          </div>

          {/* Interaction Stage */}
          <div className="ch-tx-stage">
            <Mafs viewBox={viewBox} pan={false} zoom={false}>
              <Coordinates.Cartesian />

              {/* Pre-image */}
              <Polygon
                  points={beforePts}
                  color={Theme.gray}
                  fillOpacity={0.05}
                  strokeOpacity={0.4}
              />

              {/* Image */}
              <Polygon
                  points={afterPts}
                  color={Theme.indigo}
                  fillOpacity={0.15}
                  strokeOpacity={0.9}
              />

              {/* Mapping Logic */}
              {beforePts.map((p, i) => (
                  <React.Fragment key={`map-${i}`}>
                    <Line.Segment
                        point1={p}
                        point2={afterPts[i] || p}
                        color={Theme.orange}
                        style="dashed"
                    />
                    <Text x={p[0]} y={p[1]} attach="nw" size={12} color={Theme.gray}>
                      {letters[i]}
                    </Text>
                    {afterPts[i] && (
                        <Text
                            x={afterPts[i][0]}
                            y={afterPts[i][1]}
                            attach="se"
                            size={12}
                            color={Theme.indigo}
                        >
                          {letters[i]}′
                        </Text>
                    )}
                  </React.Fragment>
              ))}
            </Mafs>

            {/* Note: pan/zoom are disabled, so don't claim it's draggable */}
            <div className="ch-tx-stage-hint">
              Use <strong>Apply Step</strong> or <strong>Play All</strong> to progress.
            </div>
          </div>

          {/* Controls Panel */}
          <div className="flexSpaceBetween">
            <Space>
              <Button
                  type="primary"
                  icon={<StepForwardOutlined />}
                  onClick={() => setAppliedSteps((s) => Math.min(s + 1, ops.length))}
                  disabled={appliedSteps >= ops.length}
              >
                Apply Step
              </Button>

              <Button
                  icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                  onClick={isPlaying ? stopPlayback : onPlay}
                  disabled={appliedSteps >= ops.length && !isPlaying}
              >
                {isPlaying ? "Pause" : "Play All"}
              </Button>
            </Space>

            <Button icon={<TableOutlined />} onClick={() => setShowTable(!showTable)}>
              {showTable ? "Hide Coordinates" : "Show Coordinate Proof"}
            </Button>
          </div>

          {/* Coordinate Proof Table */}
          {showTable && (
              <div className="ch-tx-table-wrap animate-slide-up">
                <table className="ch-tx-table">
                  <thead>
                  <tr>
                    <th>Vertex</th>
                    <th>Pre-image (x, y)</th>
                    <th>Image (x′, y′)</th>
                  </tr>
                  </thead>
                  <tbody>
                  {beforePts.map((p, i) => (
                      <tr key={i}>
                        <td>
                          <AntText strong>
                            {letters[i]} → {letters[i]}′
                          </AntText>
                        </td>
                        <td>
                          ({p[0].toFixed(1)}, {p[1].toFixed(1)})
                        </td>
                        <td>
                          ({(afterPts[i]?.[0] ?? p[0]).toFixed(1)},{" "}
                          {(afterPts[i]?.[1] ?? p[1]).toFixed(1)})
                        </td>
                      </tr>
                  ))}
                  </tbody>
                </table>
              </div>
          )}
        </Space>
      </div>
  );
};

export default TransformationGrid;
